/**
 * Pure helpers shared by the history charts on the app page (rank, estimates,
 * ratings). They lived inside app-charts.tsx until a second chart needed them.
 */

/** Percentage move between the first and last point of a series. For `position` a lower number is the better one. */
export function calculateChange(data: readonly Record<string, unknown>[], key: string): { percentage: number; isPositive: boolean } {
	if (!data || data.length < 2) return { percentage: 0, isPositive: true };
	const oldestValue = Number(data[0][key]);
	const newestValue = Number(data[data.length - 1][key]);
	const change = key === "position" ? oldestValue - newestValue : newestValue - oldestValue;
	const percentage = oldestValue !== 0 ? (change / oldestValue) * 100 : 0;
	return { percentage: Math.abs(percentage), isPositive: change > 0 };
}

/** `count` evenly spaced `date` values out of a series, for a category axis. */
export function calculateDateTicks(data: readonly { date: string }[], count: number): string[] {
	if (!data || data.length === 0) return [];
	if (data.length <= count) return data.map((item) => item.date);
	const ticks: string[] = [];
	const step = (data.length - 1) / (count - 1);
	for (let i = 0; i < count; i++) {
		const index = Math.round(i * step);
		if (index < data.length) ticks.push(data[index].date);
	}
	return ticks;
}

// "2025-10-04" -> "Oct 4", or "Oct 2025" for a series that spans more than a year.
// Parsed by hand: `new Date("2025-10-04")` is UTC midnight and shifts a day west of Greenwich.
export function formatIsoDay(iso: string, withYear = false): string {
	const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
	if (!y || !m || !d) return iso;
	const month = new Date(Date.UTC(y, m - 1, d)).toLocaleString("default", { month: "short", timeZone: "UTC" });
	return withYear ? `${month} ${y}` : `${month} ${d}`;
}

/** Tooltip label: "Oct 4, 2025" for an ISO day, anything else as it came. */
export function formatTooltipLabel(label: string | undefined): string {
	if (!label || !/^\d{4}-\d{2}-\d{2}$/.test(label)) return label ?? "";
	return `${formatIsoDay(label)}, ${label.slice(0, 4)}`;
}

export const DAY_MS = 24 * 3600 * 1000;
export const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The calendar step a daily series is ticked at, by its span in days. */
function tickStep(spanDays: number): { unit: "day"; days: number } | { unit: "month"; months: number; mid: boolean } {
	if (spanDays <= 45) return { unit: "day", days: 7 };
	if (spanDays <= 120) return { unit: "month", months: 1, mid: true };
	if (spanDays <= 400) return { unit: "month", months: 1, mid: false };
	if (spanDays <= 800) return { unit: "month", months: 2, mid: false };
	if (spanDays <= 1600) return { unit: "month", months: 3, mid: false };
	return { unit: "month", months: 6, mid: false };
}

/**
 * A readable date axis for a daily category series: ticks on calendar boundaries
 * (every 7 days over a month counting back from the newest day, the 1st and 15th
 * over a quarter, month starts over a year, then every 2, 3 or 6 months), each
 * snapped to the first plotted day on or after it. The formatter that pairs with
 * them prints "Sep 16" for day ticks and "Oct" / "Jan 2026" for month ticks, the
 * year on January and on the first tick. `calculateDateTicks` spreads five ticks
 * by index, which reads as "Jul 10, Aug 2, Aug 25" and is what the owner called
 * "needs some x-axis" (feedback 2026-09-16 item 1).
 */
export function dateAxis(data: readonly { date: string }[]): { ticks: string[]; format: (iso: string) => string } {
	const days = data.map((d) => d.date.slice(0, 10)).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
	if (days.length === 0) return { ticks: [], format: (iso) => formatIsoDay(iso) };
	const first = Date.parse(days[0]);
	const last = Date.parse(days[days.length - 1]);
	const step = tickStep((last - first) / DAY_MS);
	// Snap a wanted day to the first plotted day on or after it: within 2 days for a tick
	// labelled with its day (an unranked day or two is common), anywhere before the next
	// wanted day for one labelled by month, so a sparse series (one estimate a fortnight)
	// still gets every month.
	const byMonth = step.unit === "month" && !step.mid;
	let cursor = 0;
	const snap = (iso: string, next: string | undefined): string | null => {
		while (cursor < days.length && days[cursor] < iso) cursor++;
		if (cursor >= days.length) return null;
		const hit = days[cursor];
		if (byMonth) return !next || hit < next ? hit : null;
		return Date.parse(hit) - Date.parse(iso) <= 2 * DAY_MS ? hit : null;
	};
	const wanted: string[] = [];
	if (step.unit === "day") {
		for (let t = last; t >= first; t -= step.days * DAY_MS) wanted.unshift(toIso(t));
	} else {
		const d = new Date(first);
		let y = d.getUTCFullYear();
		let m = d.getUTCMonth();
		while (Date.UTC(y, m, 1) <= last) {
			if (m % step.months === 0) wanted.push(toIso(Date.UTC(y, m, 1)));
			if (step.mid) wanted.push(toIso(Date.UTC(y, m, 15)));
			m++;
			if (m === 12) { m = 0; y++; }
		}
	}
	const ticks: string[] = [];
	wanted.forEach((w, i) => {
		const t = snap(w, wanted[i + 1]);
		if (t && ticks[ticks.length - 1] !== t) ticks.push(t);
	});
	const format = (iso: string): string => {
		const [y, mo, dd] = iso.slice(0, 10).split("-").map(Number);
		if (!y || !mo || !dd) return iso;
		if (step.unit === "day" || step.mid) return formatIsoDay(iso);
		const month = new Date(Date.UTC(y, mo - 1, 1)).toLocaleString("default", { month: "short", timeZone: "UTC" });
		return mo === 1 || iso === ticks[0] ? `${month} ${y}` : month;
	};
	return { ticks, format };
}

/** The next "nice" ceiling at or above `v` (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 times a power of ten), for a value axis with headroom. */
export function niceCeil(v: number): number {
	if (!(v > 0)) return 1;
	const exp = 10 ** Math.floor(Math.log10(v));
	const m = v / exp;
	const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((x) => x >= m - 1e-9) ?? 10;
	return step * exp;
}

/** Axis styling shared by the app page charts: a hairline axis, tick marks, and labels that read in both themes. */
export const AXIS_STROKE = "#71717a";
export const AXIS_TICK = { fill: "#a1a1aa", fontSize: 10 } as const;

/** True when the first and last ISO days are more than a year apart. */
export function spansMoreThanAYear(data: readonly { date: string }[]): boolean {
	if (data.length < 2) return false;
	const first = Date.parse(data[0].date);
	const last = Date.parse(data[data.length - 1].date);
	return Number.isFinite(first) && Number.isFinite(last) && last - first > 365 * 24 * 3600 * 1000;
}
